import React, { useState, useMemo, useEffect } from 'react';
import { csvCell } from '../../lib/csvCell';
import { Plus, X, Search, Eye, CheckCircle, Users, Briefcase, DollarSign, Plane, AlertCircle, Upload, ChevronLeft, ChevronRight, Info, ArrowUpDown, ArrowUp, ArrowDown, Download, RotateCcw, Archive, FileText, Send, ClipboardCheck, Landmark, UserPlus } from 'lucide-react';
import { notifyToast } from '../../lib/toastStore';
import { withTeamSelection, noteTeamSelectionFailure } from '../../lib/teamSelection';
import { REFERRER_RESTRICTED_NOTICE } from '../../lib/orgApproval';
import { COUNTRIES } from '../../lib/constants';
import { firstUsablePersonName, pickNamePart } from '../../lib/nameUtils';
import SearchableSelect from '../shared/SearchableSelect';
import Modal from '../shared/Modal';
import { pageWindow } from '../../lib/pageWindow';

// PR-16 / D-25: candidate payloads do not share one name shape. Records a
// referrer adds carry top-level firstName/lastName, but link-signup and
// applicant-created drafts carry the name ONLY inside personalDetails —
// reading top-level fields alone rendered those candidates as "??" with
// blank name / country / phone. Resolve every display field through the
// shared normalizer plus personalDetails fallbacks.
const candidateDisplayName = (c: any): string => firstUsablePersonName([c], '');
const candidateInitials = (c: any): string => {
  const words = candidateDisplayName(c).split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  const first = words[0][0] || '';
  const last = words.length > 1 ? words[words.length - 1][0] || '' : '';
  return (first + last).toUpperCase() || '?';
};
const candidateCountry = (c: any): string =>
  c?.country || c?.personalDetails?.country || c?.personalDetails?.countryOfCitizenship || '';
const candidatePhone = (c: any): string =>
  c?.phone || c?.phoneNumber || c?.personalDetails?.phone || c?.personalDetails?.mobileNumber || c?.personalDetails?.phoneNumber || '';
// Sweep §1.6 / A3: the identifier shown for a candidate is the FULL
// application identifier — the stored applicantId when present, else the
// record's applicationId, else the full document id. Never truncated: the
// old 8-character doc-id fallback rendered application 104837308 as
// "10483730", an id that matches nothing.
export const candidateDisplayId = (c: any): string =>
  c?.applicantId || c?.applicationId || c?.id || '';
import { useModalLayer } from '../../lib/modalStack';
import {
  WORKFLOW_STAGES,
  STAGE_ORDER,
  STAGE_LABELS,
  STAGE_BADGE_CLASSES,
  STAGE_SYNOPSIS,
  resolveCandidateStage,
  candidateStageLabel,
  type CanonicalStage,
} from '../../lib/referrerStages';

interface Props {
  candidates: any[];
  candidateSearch: string;
  setCandidateSearch: (v: string) => void;
  /** Programs combobox state — never leave it stuck on "Loading...". */
  programsLoading?: boolean;
  programsError?: boolean;
  onRetryPrograms?: () => void;
  showAddCandidate: boolean;
  setShowAddCandidate: (v: boolean) => void;
  candidateForm: any;
  setCandidateForm: (v: any) => void;
  referrerPrograms: any[];
  addingCandidate: boolean;
  emailCheckMsg: string;
  setEmailCheckMsg: (v: string) => void;
  slideInCandidate: any;
  setSlideInCandidate: (v: any) => void;
  /**
   * PR-13b affordance honesty: restricted referrer (same signal as the
   * dashboard banner) — Add Candidate / Import CSV render DISABLED with
   * the restriction reason; the candidate list itself stays fully usable
   * read-only.
   */
  restricted?: boolean;
  slideInLoading: boolean;
  handleAddCandidate: (e: React.FormEvent) => void;
  /** §2.3 success state: set by the parent after a successful add. */
  addSuccess?: { candidate: any; sendInvite: boolean } | null;
  onAddAnother?: () => void;
  onAddSuccessDone?: () => void;
  onViewAddedCandidate?: (c: any) => void;
  onDismissAddSuccess?: () => void;
  openSlideIn: (c: any) => Promise<void>;
  closeSlideIn: () => void;
  getToken?: () => Promise<string | null>;
  onFetchCandidates?: () => Promise<void>;
}

/**
 * §2.2: the stage shown everywhere in this view is the candidate's canonical
 * workflow stage (one of the 8), resolved from the workflow record via
 * src/lib/referrerStages. The retired referrer-only vocabulary
 * (Qualified / Partner Assigned / Placement Confirmed / Financially Cleared /
 * Visa Status / Departed) is gone. Icons are presentation-only, keyed by the
 * canonical stage.
 */
const STAGE_ICONS: Record<CanonicalStage, any> = {
  draft: FileText,
  submitted: Send,
  readyForScreening: ClipboardCheck,
  readyForProcessing: Briefcase,
  billings: DollarSign,
  immigration: Landmark,
  preDeparture: Plane,
  closed: CheckCircle,
};

export default function ReferrerCandidatesView(props: Props) {
  const [localPage, setLocalPage] = useState(1);
  const itemsPerPage = 15;

  // CSV Import States
  const [showImportWizard, setShowImportWizard] = useState(false);

  const [importStep, setImportStep] = useState(1);
  const [importProgramId, setImportProgramId] = useState('');
  const [importTermId, setImportTermId] = useState('');
  const [importHeaders, setImportHeaders] = useState<string[]>([]);
  const [importRows, setImportRows] = useState<string[][]>([]);
  const [importMapping, setImportMapping] = useState<Record<string, number>>({
    firstName: -1,
    lastName: -1,
    phone: -1,
    email: -1
  });
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState<any>(null);

  // Filter & Selection & Sort States
  const [filterProgram, setFilterProgram] = useState('All');
  const [filterTerm, setFilterTerm] = useState('All');
  const [filterCountry, setFilterCountry] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');
  const [showArchived, setShowArchived] = useState(false);

  const [sortField, setSortField] = useState<string>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [selectedCandidateIds, setSelectedCandidateIds] = useState<string[]>([]);

  const isAnyFilterActive = useMemo(() => {
    return (props.candidateSearch || '').trim().length > 0 || filterProgram !== 'All' || filterTerm !== 'All' || filterCountry !== 'All' || filterStatus !== 'All' || showArchived;
  }, [props.candidateSearch, filterProgram, filterTerm, filterCountry, filterStatus, showArchived]);

  const clearAllFilters = () => {
    props.setCandidateSearch('');
    setFilterProgram('All');
    setFilterTerm('All');
    setFilterCountry('All');
    setFilterStatus('All');
    setShowArchived(false);
    setLocalPage(1);
  };

  const toggleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleBulkExportCSV = () => {
    const selected = props.candidates.filter((c: any) => selectedCandidateIds.includes(c.id || c.applicationId));
    if (selected.length === 0) return;

    const headers = ['Applicant ID', 'First Name', 'Last Name', 'Email', 'Phone', 'Program', 'Term', 'Country', 'Stage'];
    const rows = selected.map((c: any) => [
      c.applicantId || c.id || '',
      pickNamePart(c.firstName, c.personalDetails?.firstName) || candidateDisplayName(c),
      pickNamePart(c.lastName, c.personalDetails?.lastName),
      c.userEmail || c.email || '',
      candidatePhone(c),
      c.programType || c.programId || '',
      c.termLabel || c.programTerm || '',
      candidateCountry(c),
      candidateStageLabel(c)
    ]);

    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.map(csvCell).join(','), ...rows.map(r => r.map(cell => csvCell(cell)).join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `Referred_Candidates_Export_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    notifyToast('success', `Exported ${selected.length} candidate(s) to CSV`);
  };

  // Parse CSV function
  const parseCSV = (text: string): string[][] => {
    const lines = text.split(/\r?\n/).filter(line => line.trim());
    return lines.map(line => {
      const result = [];
      let current = '';
      let inQuotes = false;
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          result.push(current);
          current = '';
        } else {
          current += char;
        }
      }
      result.push(current);
      return result.map(s => s.trim().replace(/^"|"$/g, ''));
    });
  };

  const handleCSVUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      notifyToast('error', 'CSV file size exceeds 10MB limit. Please upload a smaller file.');
      return;
    }

    if (!file.name.toLowerCase().endsWith('.csv') && file.type !== 'text/csv' && file.type !== 'application/vnd.ms-excel') {
      notifyToast('error', 'Please upload a valid .csv file.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      const parsed = parseCSV(text);
      if (parsed.length > 0) {
        const headers = parsed[0];
        const rows = parsed.slice(1).filter(r => r.length === headers.length || r.some(cell => cell.trim()));
        setImportHeaders(headers);
        setImportRows(rows);

        // Attempt smart column mapping auto-detection
        const mapping: Record<string, number> = { firstName: -1, lastName: -1, phone: -1, email: -1 };
        headers.forEach((h, idx) => {
          const name = h.toLowerCase();
          if (name.includes('first') || (name.includes('name') && !name.includes('last'))) {
            mapping.firstName = idx;
          } else if (name.includes('last')) {
            mapping.lastName = idx;
          } else if (name.includes('email') || name.includes('mail')) {
            mapping.email = idx;
          } else if (name.includes('phone') || name.includes('mobile') || name.includes('tel')) {
            mapping.phone = idx;
          }
        });
        setImportMapping(mapping);
        setImportStep(3);
        notifyToast('info', `CSV loaded: ${rows.length} row(s) detected.`);
      } else {
        notifyToast('error', 'CSV file appears to be empty or unreadable.');
      }
    };
    reader.onerror = () => {
      notifyToast('error', 'Failed to read CSV file.');
    };
    reader.readAsText(file);
  };

  const executeCandidateImport = async () => {
    // PR-13b courtesy guard (mirrors handleAddCandidate): the toolbar
    // button that opens this wizard renders disabled while restricted;
    // never fire the import if the wizard was somehow already open.
    if (props.restricted) { notifyToast('error', REFERRER_RESTRICTED_NOTICE); return; }
    if (!importProgramId) return;
    setImportLoading(true);

    const mappedCandidates = importRows.map(row => {
      const email = importMapping.email >= 0 ? row[importMapping.email] : '';
      const firstName = importMapping.firstName >= 0 ? row[importMapping.firstName] : '';
      const lastName = importMapping.lastName >= 0 ? row[importMapping.lastName] : '';
      const phone = importMapping.phone >= 0 ? row[importMapping.phone] : '';

      return { firstName, lastName, email, phone };
    }).filter(c => c.firstName && c.email); // Mandatory fields filter

    try {
      const token = await getToken?.() ?? '';
      const headers: any = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;

      const res = await fetch(withTeamSelection('/api/referrer/candidates/import'), {
        method: 'POST',
        headers,
        body: JSON.stringify({
          programId: importProgramId,
          termId: importTermId,
          candidates: mappedCandidates
        })
      });

      const data = await res.json();
      if (res.ok) {
        setImportResult(data.stats);
        setImportStep(5);
        notifyToast('success', `Import complete! ${data.stats?.created || 0} candidate(s) imported.`);
        if (onFetchCandidates) {
          await onFetchCandidates();
        } else {
          window.location.reload();
        }
      } else {
        noteTeamSelectionFailure('/api/referrer/candidates/import', res.status, data);
        notifyToast('error', data.error || 'Import failed');
      }
    } catch (err: any) {
      console.error(err);
      notifyToast('error', 'Import failed. Please try again.');
    } finally {
      setImportLoading(false);
    }
  };

  const {
    candidates, candidateSearch, setCandidateSearch,
    showAddCandidate, setShowAddCandidate,
    candidateForm, setCandidateForm,
    referrerPrograms, addingCandidate,
    programsLoading, programsError, onRetryPrograms,
    emailCheckMsg, setEmailCheckMsg,
    slideInCandidate, setSlideInCandidate, slideInLoading,
    handleAddCandidate, openSlideIn, closeSlideIn,
    addSuccess, onAddAnother, onAddSuccessDone, onViewAddedCandidate, onDismissAddSuccess,
    getToken, onFetchCandidates, restricted,
  } = props;

  // §2.3: inline validation — no native browser bubbles. Errors render under
  // the field they belong to and clear as the user fixes them.
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const clearFieldError = (field: string) =>
    setFormErrors(prev => (prev[field] ? { ...prev, [field]: '' } : prev));
  const validateAndSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs: Record<string, string> = {};
    if (!candidateForm.firstName?.trim()) errs.firstName = 'First name is required.';
    if (!candidateForm.lastName?.trim()) errs.lastName = 'Last name is required.';
    const email = (candidateForm.email || '').trim();
    if (!email) errs.email = 'Email is required.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errs.email = 'Enter a valid email address.';
    if (!candidateForm.programType) errs.programType = 'Select a program.';
    else if (!candidateForm.programTerm) errs.programTerm = 'Select a term for this program.';

    const selectedProg = referrerPrograms.find((p: any) => p.id === candidateForm.programType);
    const visibleVars = Array.isArray(selectedProg?.pricingVariables)
      ? selectedProg.pricingVariables.filter((v: any) => v.visibility === 'visible')
      : [];
    if (visibleVars.length > 0 && !candidateForm.pricingVariableId) {
      errs.pricingVariableId = 'Select a pricing option for this program.';
    }
    if (!candidateForm.country) errs.country = 'Select a country.';
    setFormErrors(errs);
    if (Object.keys(errs).length > 0) return;
    handleAddCandidate(e);
  };
  const fieldError = (field: string) =>
    formErrors[field] ? <p id={`add-candidate-error-${field}`} role="alert" className="text-[11px] font-medium text-red-600 mt-1">{formErrors[field]}</p> : null;
  const inputCls = (field: string) =>
    `w-full px-3 py-3 md:py-2.5 text-base md:text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 disabled:opacity-50 ${formErrors[field] ? 'border-red-300 bg-red-50/40' : 'border-slate-200'}`;
  const closeAddModal = () => { setShowAddCandidate(false); setEmailCheckMsg(''); setFormErrors({}); onDismissAddSuccess?.(); };

  // Compute unique filter options dynamically from candidates
  const uniqueTerms = useMemo(() => {
    const set = new Set<string>();
    candidates.forEach(c => {
      const t = c.termLabel || c.programTerm || c.term;
      if (t) set.add(t);
    });
    return Array.from(set).sort();
  }, [candidates]);

  const uniqueCountries = useMemo(() => {
    const set = new Set<string>();
    candidates.forEach(c => {
      if (c.country) set.add(c.country);
    });
    return Array.from(set).sort();
  }, [candidates]);

  const paginated = useMemo(() => {
    let filtered = candidates;

    // 0. Archived candidates are hidden by default (toggle to reveal them)
    if (!showArchived) {
      filtered = filtered.filter((c: any) => c.status !== 'archived' && c.isArchived !== true);
    }

    // 1. Text Search
    if (candidateSearch.trim()) {
      const q = candidateSearch.toLowerCase();
      filtered = filtered.filter((c: any) =>
        candidateDisplayName(c).toLowerCase().includes(q) ||
        (c.email || '').toLowerCase().includes(q) ||
        (c.applicantId || '').toLowerCase().includes(q) ||
        (c.programType || '').toLowerCase().includes(q)
      );
    }

    // 2. Program Filter
    if (filterProgram !== 'All') {
      filtered = filtered.filter((c: any) => c.programId === filterProgram || c.programType === filterProgram);
    }

    // 3. Term Filter
    if (filterTerm !== 'All') {
      filtered = filtered.filter((c: any) => (c.termLabel || c.programTerm || c.term) === filterTerm);
    }

    // 4. Country Filter
    if (filterCountry !== 'All') {
      filtered = filtered.filter((c: any) => candidateCountry(c) === filterCountry);
    }

    // 5. Workflow Stage Filter — canonical 8-stage vocabulary (§2.2)
    if (filterStatus !== 'All') {
      filtered = filtered.filter((c: any) => resolveCandidateStage(c) === filterStatus);
    }

    // 6. Sorting
    filtered = [...filtered].sort((a: any, b: any) => {
      let aVal = '';
      let bVal = '';
      if (sortField === 'name') {
        aVal = candidateDisplayName(a).toLowerCase();
        bVal = candidateDisplayName(b).toLowerCase();
      } else if (sortField === 'applicantId') {
        aVal = (a.applicantId || a.id || '').toLowerCase();
        bVal = (b.applicantId || b.id || '').toLowerCase();
      } else if (sortField === 'email') {
        aVal = (a.userEmail || a.email || '').toLowerCase();
        bVal = (b.userEmail || b.email || '').toLowerCase();
      } else if (sortField === 'program') {
        aVal = (a.programType || a.programId || '').toLowerCase();
        bVal = (b.programType || b.programId || '').toLowerCase();
      } else if (sortField === 'country') {
        aVal = candidateCountry(a).toLowerCase();
        bVal = candidateCountry(b).toLowerCase();
      } else if (sortField === 'stage') {
        const aMs = STAGE_ORDER.indexOf(resolveCandidateStage(a));
        const bMs = STAGE_ORDER.indexOf(resolveCandidateStage(b));
        return sortDirection === 'asc' ? aMs - bMs : bMs - aMs;
      }
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    const totalPages = Math.ceil(filtered.length / itemsPerPage) || 1;
    const start = (localPage - 1) * itemsPerPage;
    return { items: filtered.slice(start, start + itemsPerPage), total: filtered.length, totalPages };
  }, [candidates, candidateSearch, filterProgram, filterTerm, filterCountry, filterStatus, showArchived, sortField, sortDirection, localPage]);

  // Clamp the active page when the dataset shrinks (add/delete/archive/import)
  // so we never render an empty page.
  useEffect(() => {
    setLocalPage(p => Math.min(p, paginated.totalPages));
  }, [paginated.totalPages]);

  // Escape closes the slide-in drawer — top-guarded by the modal stack
  // so nested popups above it close first. (The Add Candidate dialog
  // and the Import Wizard register their own layers through the
  // shared Modal since REF-16.)
  const escapeClose = () => { closeSlideIn(); closeAddModal(); };
  const slideZ = useModalLayer(!!props.slideInCandidate, escapeClose);

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Candidate Management</h1>
          <p className="text-sm text-slate-500">Manage and track all your referred candidates</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => {
            setImportStep(1);
            setImportProgramId('');
            setImportTermId('');
            setImportHeaders([]);
            setImportRows([]);
            setImportResult(null);
            setShowImportWizard(true);
          }}
            disabled={!!restricted}
            title={restricted ? REFERRER_RESTRICTED_NOTICE : undefined}
            className="flex items-center justify-center space-x-2 px-4 py-2.5 bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 rounded-xl text-sm font-semibold transition cursor-pointer shadow-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white">
            <Upload className="h-4 w-4 text-slate-500" /><span>Import CSV</span>
          </button>
          <button onClick={() => { setShowAddCandidate(true); setEmailCheckMsg(''); }}
            disabled={!!restricted}
            title={restricted ? REFERRER_RESTRICTED_NOTICE : undefined}
            className="flex items-center justify-center space-x-2 px-4 py-2.5 btn-primary  text-white rounded-xl text-sm font-semibold transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
            <Plus className="h-4 w-4" /><span>Add Candidate</span>
          </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm space-y-3">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input type="text" value={candidateSearch}
              onChange={e => { setCandidateSearch(e.target.value); setLocalPage(1); }}
              aria-label="Search candidates by name, email, ID or program"
              placeholder="Search by name, email, ID or program..."
              className="w-full pl-10 pr-4 py-3 md:py-2 text-base md:text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20  bg-slate-50 font-medium" />
          </div>
          <div className="grid grid-cols-2 md:flex items-center gap-3">
            {/* Program Filter */}
            <div className="w-full max-md:min-w-0 md:w-52">
              <SearchableSelect
                value={filterProgram === 'All' ? '' : filterProgram}
                onChange={v => { setFilterProgram(v || 'All'); setLocalPage(1); }}
                options={referrerPrograms.map((p: any) => ({
                  value: p.id,
                  label: p.name || p.code,
                }))}
                ariaLabel="Filter by program"
                placeholder="Search programs…"
                clearLabel="All Programs"
                emptyLabel="No programs found"
              />
            </div>

            {/* Term Filter */}
            <select
              aria-label="Filter by term"
              value={filterTerm}
              onChange={e => { setFilterTerm(e.target.value); setLocalPage(1); }}
              className="max-md:w-full max-md:min-w-0 px-2.5 py-3 md:py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-base md:text-xs font-bold outline-none focus:bg-white"
            >
              <option value="All">All Terms</option>
              {uniqueTerms.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>

            {/* Country Filter */}
            <select
              aria-label="Filter by country"
              value={filterCountry}
              onChange={e => { setFilterCountry(e.target.value); setLocalPage(1); }}
              className="max-md:w-full max-md:min-w-0 px-2.5 py-3 md:py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-base md:text-xs font-bold outline-none focus:bg-white"
            >
              <option value="All">All Countries</option>
              {uniqueCountries.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>

            {/* Workflow Stage Filter — the canonical 8 stages (§2.2) */}
            <select
              aria-label="Filter by workflow stage"
              value={filterStatus}
              onChange={e => { setFilterStatus(e.target.value); setLocalPage(1); }}
              className="max-md:w-full max-md:min-w-0 px-2.5 py-3 md:py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-base md:text-xs font-bold outline-none focus:bg-white"
            >
              <option value="All">All Stages</option>
              {WORKFLOW_STAGES.map((s) => (
                <option key={s.key} value={s.key}>{s.label}</option>
              ))}
            </select>

            {/* Archived toggle */}
            <button
              onClick={() => { setShowArchived(prev => !prev); setLocalPage(1); }}
              className={`px-2.5 py-1.5 min-h-[44px] md:min-h-[32px] rounded-lg text-xs font-bold transition flex items-center space-x-1 cursor-pointer border ${
                showArchived
                  ? 'bg-amber-50 text-amber-700 border-amber-200'
                  : 'bg-slate-50 text-slate-500 border-slate-200 hover:text-slate-700 hover:bg-slate-100'
              }`}
              title="Toggle archived candidates"
            >
              <Archive className="h-3 w-3" />
              <span>{showArchived ? 'Archived Shown' : 'Show Archived'}</span>
            </button>

            {isAnyFilterActive && (
              <button
                onClick={clearAllFilters}
                className="px-2.5 py-1.5 min-h-[44px] md:min-h-[32px] bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 rounded-lg text-xs font-bold transition flex items-center space-x-1 cursor-pointer"
                title="Clear all active filters"
              >
                <RotateCcw className="h-3 w-3" />
                <span>Clear</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {selectedCandidateIds.length > 0 && (
        <div className="flex items-center justify-between bg-blue-50 border border-blue-200 text-slate-700 px-4 py-3 rounded-xl shadow-sm animate-fade-in">
          <div className="flex items-center space-x-3">
            <span className="bg-blue-600 text-white font-bold text-xs px-2.5 py-1 rounded-lg">
              {selectedCandidateIds.length} Selected
            </span>
            <span className="text-xs text-slate-500">Bulk action options for selected candidates</span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleBulkExportCSV}
              className="px-3 py-1.5 min-h-[44px] md:min-h-[32px] bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-lg transition flex items-center space-x-1 cursor-pointer"
            >
              <Download className="h-3.5 w-3.5" />
              <span>Export Selected CSV</span>
            </button>
            <button
              onClick={() => setSelectedCandidateIds([])}
              className="px-3 py-1.5 min-h-[44px] md:min-h-[32px] bg-white hover:bg-blue-100 text-slate-600 border border-blue-200 text-xs font-bold rounded-lg transition cursor-pointer"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Desktop table (≥ md — the same breakpoint as the sidebar/bottom-nav
          switch, §2.7). Below md the card list renders instead, so the table
          never forces horizontal scrolling on phones. */}
      <div className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-100 overflow-hidden hidden md:block">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider font-semibold">
              <tr>
                <th className="pl-4 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={paginated.items.length > 0 && paginated.items.every((c: any) => selectedCandidateIds.includes(c.id || c.applicationId))}
                    onChange={() => {
                      const pageIds = paginated.items.map((c: any) => c.id || c.applicationId);
                      const allSelected = pageIds.every(id => selectedCandidateIds.includes(id));
                      if (allSelected) {
                        setSelectedCandidateIds(prev => prev.filter(id => !pageIds.includes(id)));
                      } else {
                        setSelectedCandidateIds(prev => Array.from(new Set([...prev, ...pageIds])));
                      }
                    }}
                    className="rounded border-slate-300 text-indigo-600 focus:ring-brand-primary cursor-pointer"
                  />
                </th>
                {/* REF-11: sort controls are real buttons inside the
                    headers (keyboard and screen-reader operable), with
                    aria-sort on the active column's th. */}
                <th className="hover:bg-slate-100 transition" aria-sort={sortField === 'applicantId' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : undefined}>
                  <button type="button" onClick={() => toggleSort('applicantId')} aria-label="Sort by Applicant ID" className="flex items-center space-x-1 w-full px-4 py-3 cursor-pointer text-left">
                    <span>Applicant ID</span>
                    {sortField === 'applicantId' ? (sortDirection === 'asc' ? <ArrowUp className="h-3 w-3 text-indigo-600" /> : <ArrowDown className="h-3 w-3 text-indigo-600" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
                  </button>
                </th>
                <th className="hover:bg-slate-100 transition" aria-sort={sortField === 'name' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : undefined}>
                  <button type="button" onClick={() => toggleSort('name')} aria-label="Sort by Name" className="flex items-center space-x-1 w-full px-4 py-3 cursor-pointer text-left">
                    <span>Name</span>
                    {sortField === 'name' ? (sortDirection === 'asc' ? <ArrowUp className="h-3 w-3 text-indigo-600" /> : <ArrowDown className="h-3 w-3 text-indigo-600" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
                  </button>
                </th>
                <th className="hover:bg-slate-100 transition" aria-sort={sortField === 'email' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : undefined}>
                  <button type="button" onClick={() => toggleSort('email')} aria-label="Sort by Email" className="flex items-center space-x-1 w-full px-4 py-3 cursor-pointer text-left">
                    <span>Email</span>
                    {sortField === 'email' ? (sortDirection === 'asc' ? <ArrowUp className="h-3 w-3 text-indigo-600" /> : <ArrowDown className="h-3 w-3 text-indigo-600" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
                  </button>
                </th>
                <th className="hover:bg-slate-100 transition" aria-sort={sortField === 'program' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : undefined}>
                  <button type="button" onClick={() => toggleSort('program')} aria-label="Sort by Program" className="flex items-center space-x-1 w-full px-4 py-3 cursor-pointer text-left">
                    <span>Program</span>
                    {sortField === 'program' ? (sortDirection === 'asc' ? <ArrowUp className="h-3 w-3 text-indigo-600" /> : <ArrowDown className="h-3 w-3 text-indigo-600" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
                  </button>
                </th>
                <th className="px-4 py-3">Term</th>
                <th className="hover:bg-slate-100 transition" aria-sort={sortField === 'country' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : undefined}>
                  <button type="button" onClick={() => toggleSort('country')} aria-label="Sort by Country" className="flex items-center space-x-1 w-full px-4 py-3 cursor-pointer text-left">
                    <span>Country</span>
                    {sortField === 'country' ? (sortDirection === 'asc' ? <ArrowUp className="h-3 w-3 text-indigo-600" /> : <ArrowDown className="h-3 w-3 text-indigo-600" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
                  </button>
                </th>
                <th className="hover:bg-slate-100 transition" aria-sort={sortField === 'stage' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : undefined}>
                  <button type="button" onClick={() => toggleSort('stage')} aria-label="Sort by Stage" className="flex items-center space-x-1 w-full px-4 py-3 cursor-pointer text-left">
                    <span>Stage</span>
                    {sortField === 'stage' ? (sortDirection === 'asc' ? <ArrowUp className="h-3 w-3 text-indigo-600" /> : <ArrowDown className="h-3 w-3 text-indigo-600" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
                  </button>
                </th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {paginated.items.map((c: any) => {
                const targetId = c.id || c.applicationId;
                const isSelected = selectedCandidateIds.includes(targetId);
                return (
                  <tr key={c.id} className={`hover:bg-slate-50 transition cursor-pointer ${isSelected ? 'bg-indigo-50/40' : ''}`} onClick={() => openSlideIn(c)}>
                    <td className="pl-4 py-3" onClick={e => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {
                          setSelectedCandidateIds(prev =>
                            prev.includes(targetId) ? prev.filter(id => id !== targetId) : [...prev, targetId]
                          );
                        }}
                        className="rounded border-slate-300 text-indigo-600 focus:ring-brand-primary cursor-pointer"
                      />
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-500">{candidateDisplayId(c)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-brand text-xs font-bold overflow-hidden">
                          {(c.profilePhotoUrl || c.photoUrl || c.personalDetails?.profilePhotoUrl || c.personalDetails?.profilePhoto) ? (
                            <img src={c.profilePhotoUrl || c.photoUrl || c.personalDetails?.profilePhotoUrl || c.personalDetails?.profilePhoto} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <>{candidateInitials(c)}</>
                          )}
                        </div>
                        <p className="font-semibold text-slate-900">{candidateDisplayName(c)}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-600 text-xs">{c.userEmail || c.email || ''}</td>
                    <td className="px-4 py-3"><span className="px-2 py-0.5 bg-brand-active rounded text-[10px] font-semibold">{c.programType || ''}</span></td>
                    <td className="px-4 py-3 text-slate-600 text-xs font-semibold">{c.termLabel || c.programTerm || c.term || '—'}</td>
                    <td className="px-4 py-3 text-slate-600">{candidateCountry(c)}</td>
                    <td className="px-4 py-3">
                      {(() => {
                        const stageKey = resolveCandidateStage(c);
                        return (
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-semibold ${STAGE_BADGE_CLASSES[stageKey]}`}>
                            {STAGE_LABELS[stageKey]}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={(e) => { e.stopPropagation(); openSlideIn(c); }}
                        aria-label={`View details for ${candidateDisplayName(c)}`}
                        className="p-2 text-slate-400 hover:text-brand bg-brand-active rounded-lg transition cursor-pointer" title="View details">
                        <Eye className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
              {paginated.items.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-500 text-sm">
                  {candidates.length === 0
                    ? 'No candidates yet — add your first candidate to start tracking their progress here.'
                    : 'No candidates match your current filters.'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile cards (< md) — same page of candidates, no horizontal scroll,
          the whole card opens the candidate (§2.2). */}
      <div className="md:hidden space-y-3">
        {paginated.items.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-100 p-8 text-center">
            <Users className="h-8 w-8 text-slate-300 mx-auto mb-2" />
            <p className="text-sm font-semibold text-slate-700">
              {candidates.length === 0 ? 'No candidates yet' : 'No candidates match your filters'}
            </p>
            <p className="text-xs text-slate-500 mt-1">
              {candidates.length === 0
                ? 'Add your first candidate to start tracking their progress here.'
                : 'Try clearing a filter or searching for something else.'}
            </p>
            {candidates.length === 0 ? (
              <button
                onClick={() => { setShowAddCandidate(true); setEmailCheckMsg(''); }}
                disabled={!!restricted}
                title={restricted ? REFERRER_RESTRICTED_NOTICE : undefined}
                className="mt-4 inline-flex items-center gap-1.5 px-4 py-2.5 btn-primary text-white rounded-xl text-sm font-semibold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <UserPlus className="h-4 w-4" /> Add Candidate
              </button>
            ) : isAnyFilterActive ? (
              <button
                onClick={clearAllFilters}
                className="mt-4 inline-flex items-center gap-1.5 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-sm font-semibold cursor-pointer"
              >
                <RotateCcw className="h-4 w-4" /> Clear filters
              </button>
            ) : null}
          </div>
        ) : (
          paginated.items.map((c: any) => {
            const stageKey = resolveCandidateStage(c);
            return (
              <button
                key={c.id}
                onClick={() => openSlideIn(c)}
                className="w-full text-left bg-white rounded-2xl shadow-sm ring-1 ring-slate-100 p-4 active:bg-slate-50 transition cursor-pointer"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-brand text-xs font-bold overflow-hidden shrink-0">
                    {(c.profilePhotoUrl || c.photoUrl || c.personalDetails?.profilePhotoUrl || c.personalDetails?.profilePhoto) ? (
                      <img src={c.profilePhotoUrl || c.photoUrl || c.personalDetails?.profilePhotoUrl || c.personalDetails?.profilePhoto} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <>{candidateInitials(c)}</>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-semibold text-slate-900 truncate">{candidateDisplayName(c)}</p>
                      <ChevronRight className="h-4 w-4 text-slate-300 shrink-0" />
                    </div>
                    <p className="text-[11px] text-slate-500 font-mono">{candidateDisplayId(c)}</p>
                    <div className="flex flex-wrap items-center gap-1.5 mt-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-semibold ${STAGE_BADGE_CLASSES[stageKey]}`}>
                        {STAGE_LABELS[stageKey]}
                      </span>
                      {c.programType && <span className="px-2 py-0.5 bg-brand-active rounded text-[10px] font-semibold">{c.programType}</span>}
                      {(c.termLabel || c.programTerm || c.term) && <span className="text-[11px] text-slate-500 font-medium">{c.termLabel || c.programTerm || c.term}</span>}
                      {candidateCountry(c) && <span className="text-[11px] text-slate-500">{candidateCountry(c)}</span>}
                    </div>
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>

      {paginated.totalPages > 1 && (
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs text-slate-500">Showing {(localPage - 1) * itemsPerPage + 1}-{Math.min(localPage * itemsPerPage, paginated.total)} of {paginated.total}</p>
          <div className="flex items-center gap-1">
            <button onClick={() => setLocalPage(p => Math.max(1, p - 1))} disabled={localPage <= 1}
              className="px-3 py-1.5 min-h-[44px] sm:min-h-[32px] inline-flex items-center justify-center text-xs font-semibold rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-40 transition cursor-pointer">Prev</button>
            {/* REF-08: below sm the compact "Page n of N" pattern (the
                Jobs pager) replaces the number row so the controls fit
                one line at 360px at any page count; at sm and up the
                windowed page list renders. */}
            <span className="sm:hidden text-xs font-semibold text-slate-600 px-1">Page {localPage} of {paginated.totalPages}</span>
            <div className="hidden sm:flex items-center gap-1">
              {pageWindow(localPage, paginated.totalPages).map((item, i) => item === 'ellipsis' ? (
                <span key={`gap-${i}`} className="px-1 text-xs font-semibold text-slate-400" aria-hidden="true">…</span>
              ) : (
                <button key={item} onClick={() => setLocalPage(item)} aria-current={item === localPage ? 'page' : undefined}
                  className={`px-3 py-1.5 min-h-[32px] min-w-[32px] inline-flex items-center justify-center text-xs font-semibold rounded-lg transition cursor-pointer ${item === localPage ? 'btn-primary text-white' : 'border border-slate-200 hover:bg-slate-50'}`}>{item}</button>
              ))}
            </div>
            <button onClick={() => setLocalPage(p => Math.min(paginated.totalPages, p + 1))} disabled={localPage >= paginated.totalPages}
              className="px-3 py-1.5 min-h-[44px] sm:min-h-[32px] inline-flex items-center justify-center text-xs font-semibold rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-40 transition cursor-pointer">Next</button>
          </div>
        </div>
      )}

      {showAddCandidate && (
        <Modal onClose={closeAddModal} ariaLabel={addSuccess ? 'Candidate Added' : 'Add New Candidate'}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90dvh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between p-5 border-b border-slate-100 shrink-0">
              <h2 className="text-lg font-bold text-slate-900">{addSuccess ? 'Candidate Added' : 'Add New Candidate'}</h2>
              <button onClick={closeAddModal} aria-label="Close" className="p-1.5 min-h-[44px] min-w-[44px] inline-flex items-center justify-center text-slate-400 hover:text-slate-600 rounded-lg cursor-pointer"><X className="h-5 w-5" /></button>
            </div>
            {addSuccess ? (
              <div className="p-5 space-y-4 overflow-y-auto">
                <div className="flex flex-col items-center text-center pt-2">
                  <div className="w-14 h-14 rounded-full bg-emerald-50 ring-4 ring-emerald-100 flex items-center justify-center mb-3">
                    <CheckCircle className="h-7 w-7 text-emerald-600" />
                  </div>
                  <p className="text-base font-bold text-slate-900">
                    {addSuccess.candidate.firstName || ''} {addSuccess.candidate.lastName || ''} is on your list
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Applicant ID: <span className="font-mono font-bold text-slate-700">{addSuccess.candidate.applicantId || addSuccess.candidate.id}</span>
                  </p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4 space-y-1.5 text-xs text-slate-600 leading-relaxed">
                  <p><span className="font-semibold text-slate-700">Program:</span> {addSuccess.candidate.programType || addSuccess.candidate.programName || '—'}{addSuccess.candidate.programTerm ? ` · ${addSuccess.candidate.programTerm}` : ''}</p>
                  {addSuccess.sendInvite ? (
                    <p><span className="font-semibold text-slate-700">What happens next:</span> we've emailed {addSuccess.candidate.userEmail || addSuccess.candidate.email || 'the candidate'} an invitation to create their account and start the application. You'll see their stage update here as they progress.</p>
                  ) : (
                    <p><span className="font-semibold text-slate-700">What happens next:</span> no invite email was sent. Share your referral link with them, or send an invitation any time from the Invitations page — they'll be linked to you when they register.</p>
                  )}
                </div>
                <div className="flex flex-col sm:flex-row justify-end gap-2 pt-1">
                  <button type="button" onClick={() => { setFormErrors({}); onAddAnother?.(); }}
                    className="px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-xl transition cursor-pointer">Add Another</button>
                  <button type="button" onClick={() => onViewAddedCandidate?.(addSuccess.candidate)}
                    className="px-4 py-2.5 text-sm font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-xl transition cursor-pointer">View Candidate</button>
                  <button type="button" onClick={() => { setFormErrors({}); onAddSuccessDone?.(); }}
                    className="px-4 py-2.5 text-sm font-semibold btn-primary text-white rounded-xl transition shadow-lg shadow-blue-600/20 cursor-pointer">Done</button>
                </div>
              </div>
            ) : (
            <form onSubmit={validateAndSubmit} noValidate className="flex flex-col min-h-0 flex-1">
              <div className="p-5 space-y-4 overflow-y-auto">
              {emailCheckMsg && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                  <p className="text-xs text-red-700">{emailCheckMsg}</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label htmlFor="add-candidate-firstName" className="text-xs font-semibold text-slate-600">First Name *</label>
                  <input id="add-candidate-firstName" aria-invalid={!!formErrors.firstName} aria-describedby={formErrors.firstName ? 'add-candidate-error-firstName' : undefined} disabled={addingCandidate} value={candidateForm.firstName} onChange={e => { setCandidateForm({ ...candidateForm, firstName: e.target.value }); clearFieldError('firstName'); }}
                    className={inputCls('firstName')} />
                  {fieldError('firstName')}
                </div>
                <div className="space-y-1">
                  <label htmlFor="add-candidate-lastName" className="text-xs font-semibold text-slate-600">Last Name *</label>
                  <input id="add-candidate-lastName" aria-invalid={!!formErrors.lastName} aria-describedby={formErrors.lastName ? 'add-candidate-error-lastName' : undefined} disabled={addingCandidate} value={candidateForm.lastName} onChange={e => { setCandidateForm({ ...candidateForm, lastName: e.target.value }); clearFieldError('lastName'); }}
                    className={inputCls('lastName')} />
                  {fieldError('lastName')}
                </div>
              </div>
              <div className="space-y-1">
                <label htmlFor="add-candidate-email" className="text-xs font-semibold text-slate-600">Email *</label>
                <input id="add-candidate-email" aria-invalid={!!formErrors.email} aria-describedby={formErrors.email ? 'add-candidate-error-email' : undefined} type="email" disabled={addingCandidate} value={candidateForm.email} onChange={e => { setCandidateForm({ ...candidateForm, email: e.target.value }); setEmailCheckMsg(''); clearFieldError('email'); }}
                  className={inputCls('email')} />
                {fieldError('email')}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label htmlFor="add-candidate-programType" className="text-xs font-semibold text-slate-600">Program <span aria-hidden="true">*</span><span className="sr-only">(required)</span></label>
                  <SearchableSelect
                    id="add-candidate-programType"
                    ariaLabel="Program"
                    disabled={addingCandidate}
                    value={candidateForm.programType}
                    onChange={v => {
                      const selected = referrerPrograms.find((p: any) => p.id === v);
                      setCandidateForm({ ...candidateForm, programType: v, programName: selected?.name || selected?.code || '', programTerm: '', pricingVariableId: '' });
                      clearFieldError('programType'); clearFieldError('programTerm'); clearFieldError('pricingVariableId');
                    }}
                    options={referrerPrograms.filter((p: any) => p.active !== false && (p.terms || p.intakes) && (p.terms || p.intakes).some((t: any) => t.isOpen !== false)).map((p: any) => ({
                      // Gate 2 (PR-0b): when the API flags a program whose
                      // commission rate is unset, it cannot take candidates.
                      value: p.id,
                      label: `${(p.name || p.code)}${p.commissionRateSet === false ? ' — commission amount not set' : ''}`,
                      disabled: p.commissionRateSet === false,
                    }))}
                    placeholder="Search programs…"
                    clearLabel={programsLoading ? 'Loading programs...' : programsError ? 'Could not load programs' : 'Select a program...'}
                    emptyLabel={programsError ? 'Could not load programs' : programsLoading ? 'Loading programs...' : 'No programs found'}
                  />
                  {fieldError('programType')}
                  {programsError && (
                    <button type="button" onClick={() => onRetryPrograms?.()} className="mt-1 text-xs font-semibold text-blue-600 hover:text-blue-800 underline">
                      Retry loading programs
                    </button>
                  )}
                  {referrerPrograms.some((p: any) => p.commissionRateSet === false) && (
                    <p className="mt-1 text-[11px] text-amber-600">Programs marked "commission amount not set" can't accept candidates yet — your ISATS admin needs to set a commission amount for them on your referrer profile (it can be $0).</p>
                  )}
                </div>
                <div className="space-y-1">
                  <label htmlFor="add-candidate-programTerm" className="text-xs font-semibold text-slate-600">Term *</label>
                  <select id="add-candidate-programTerm" aria-invalid={!!formErrors.programTerm} aria-describedby={formErrors.programTerm ? 'add-candidate-error-programTerm' : undefined} disabled={addingCandidate || !candidateForm.programType} value={candidateForm.programTerm || ''} onChange={e => { setCandidateForm({ ...candidateForm, programTerm: e.target.value }); clearFieldError('programTerm'); }}
                    className={`${inputCls('programTerm')} bg-white`}>
                    <option value="">{candidateForm.programType ? 'Select term...' : 'Select a program first'}</option>
                    {(() => {
                      const selectedProg = referrerPrograms.find((p: any) => p.id === candidateForm.programType);
                      const progTerms: any[] = selectedProg?.terms || selectedProg?.intakes || [];
                      return progTerms.filter((t: any) => typeof t === 'string' || t.isOpen !== false).map((t: any) => {
                        const termLabel = typeof t === 'string' ? t : t.label || t.name || '';
                        const termVal = typeof t === 'string' ? t : t.termId || t.label || '';
                        return (
                          <option key={termVal} value={termVal}>{termLabel}</option>
                        );
                      });
                    })()}
                  </select>
                  {fieldError('programTerm')}
                </div>
              </div>
              {(() => {
                const selectedProg = referrerPrograms.find((p: any) => p.id === candidateForm.programType);
                const visibleVars = Array.isArray(selectedProg?.pricingVariables)
                  ? selectedProg.pricingVariables.filter((v: any) => v.visibility === 'visible')
                  : [];
                if (visibleVars.length === 0) return null;
                return (
                  <div className="space-y-1">
                    <label htmlFor="add-candidate-pricingVariableId" className="text-xs font-semibold text-slate-600">Pricing Option / Variable *</label>
                    <select
                      id="add-candidate-pricingVariableId"
                      aria-invalid={!!formErrors.pricingVariableId}
                      aria-describedby={formErrors.pricingVariableId ? 'add-candidate-error-pricingVariableId' : undefined}
                      disabled={addingCandidate}
                      value={candidateForm.pricingVariableId || ''}
                      onChange={e => {
                        setCandidateForm({ ...candidateForm, pricingVariableId: e.target.value });
                        clearFieldError('pricingVariableId');
                      }}
                      className={`${inputCls('pricingVariableId')} bg-white`}
                    >
                      <option value="">Select pricing option...</option>
                      {visibleVars.map((v: any) => (
                        <option key={v.id} value={v.id}>
                          {v.name} {v.code ? `(${v.code})` : ''} — ${Number(v.sellingPrice || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </option>
                      ))}
                    </select>
                    {fieldError('pricingVariableId')}
                  </div>
                );
              })()}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label htmlFor="add-candidate-phone" className="text-xs font-semibold text-slate-600">Phone</label>
                  <input id="add-candidate-phone" type="tel" placeholder="+1-876-555-0199" disabled={addingCandidate} value={candidateForm.phone} onChange={e => setCandidateForm({ ...candidateForm, phone: e.target.value })}
                    className="w-full px-3 py-3 md:py-2.5 text-base md:text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20  disabled:opacity-50" />
                </div>
                <div className="space-y-1">
                  <label htmlFor="add-candidate-country" className="text-xs font-semibold text-slate-600">Country *</label>
                  <select id="add-candidate-country" aria-invalid={!!formErrors.country} aria-describedby={formErrors.country ? 'add-candidate-error-country' : undefined} disabled={addingCandidate} value={candidateForm.country} onChange={e => { setCandidateForm({ ...candidateForm, country: e.target.value }); clearFieldError('country'); }}
                    className={`${inputCls('country')} bg-white`}>
                    <option value="">Select a country...</option>
                    {COUNTRIES.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                  {fieldError('country')}
                </div>
              </div>
              <div className="space-y-1">
                <label htmlFor="add-candidate-notes" className="text-xs font-semibold text-slate-600">Notes</label>
                <textarea id="add-candidate-notes" disabled={addingCandidate} value={candidateForm.notes} onChange={e => setCandidateForm({ ...candidateForm, notes: e.target.value })} rows={2}
                  className="w-full px-3 py-3 md:py-2.5 text-base md:text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20  disabled:opacity-50" />
              </div>
              <div className="flex items-center gap-3 py-1">
                <button
                  type="button"
                  aria-pressed={!!candidateForm.sendInvite}
                  aria-label="Send invite email"
                  onClick={() => setCandidateForm({ ...candidateForm, sendInvite: !candidateForm.sendInvite })}
                  disabled={addingCandidate}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer disabled:opacity-50 ${candidateForm.sendInvite ? 'btn-primary' : 'bg-slate-300'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${candidateForm.sendInvite ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
                <div className="flex flex-col">
                  <span className="text-xs font-semibold text-slate-700">Send Invite Email</span>
                  <span className="text-[11px] text-slate-500 leading-relaxed">
                    {candidateForm.sendInvite
                      ? `On: ${candidateForm.email?.trim() || 'the candidate'} gets an email inviting them to create their account and start the application right away.`
                      : 'Off: no email is sent. You can share your referral link instead, or invite them later from the Invitations page.'}
                  </span>
                </div>
              </div>
              </div>
              <div className="flex justify-end space-x-2 px-5 py-4 border-t border-slate-100 shrink-0">
                <button type="button" onClick={closeAddModal} disabled={addingCandidate}
                  className="px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-xl transition cursor-pointer disabled:opacity-50">Cancel</button>
                <button type="submit" disabled={addingCandidate}
                  className="px-4 py-2.5 text-sm font-semibold btn-primary  disabled:bg-blue-300 text-white rounded-xl transition shadow-lg shadow-blue-600/20 cursor-pointer">
                  {addingCandidate ? 'Adding...' : 'Add Candidate'}
                </button>
              </div>
            </form>
            )}
          </div>
        </Modal>
      )}

      {slideInCandidate && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex justify-end" style={{ zIndex: slideZ }} onClick={closeSlideIn}>
          <div className="relative w-full max-w-xl h-full max-h-[100dvh] bg-white shadow-2xl animate-slide-in-right flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-slate-100 p-5 flex items-center justify-between z-10 shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-brand text-sm font-bold overflow-hidden">
                  {(slideInCandidate.profilePhotoUrl || slideInCandidate.photoUrl || slideInCandidate.personalDetails?.profilePhotoUrl || slideInCandidate.personalDetails?.profilePhoto) ? (
                    <img src={slideInCandidate.profilePhotoUrl || slideInCandidate.photoUrl || slideInCandidate.personalDetails?.profilePhotoUrl || slideInCandidate.personalDetails?.profilePhoto} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <>
                      {candidateInitials(slideInCandidate)}
                    </>
                  )}
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-900">{candidateDisplayName(slideInCandidate)}</h2>
                  <p className="text-xs text-slate-500">{slideInCandidate.userEmail || slideInCandidate.email || ''}</p>
                </div>
              </div>
              <button onClick={closeSlideIn} aria-label="Close" className="p-1.5 min-h-[44px] min-w-[44px] inline-flex items-center justify-center text-slate-400 hover:text-slate-600 rounded-lg cursor-pointer"><X className="h-5 w-5" /></button>
            </div>

            {slideInLoading ? (
              <div className="flex-1 flex items-center justify-center py-20"><div className="premium-loader" /></div>
            ) : (
              <div className="flex-1 overflow-y-auto p-5 space-y-6">
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: 'Applicant ID', value: candidateDisplayId(slideInCandidate) },
                    { label: 'Program', value: slideInCandidate.programType || '' },
                    { label: 'Term', value: slideInCandidate.programTerm || '' },
                    { label: 'Country', value: candidateCountry(slideInCandidate) },
                    { label: 'Phone', value: candidatePhone(slideInCandidate) },
                  ].map((item, i) => (
                    <div key={i} className="bg-slate-50 rounded-xl p-3">
                      <p className="text-[10px] text-slate-500 uppercase font-semibold">{item.label}</p>
                      <p className="text-sm font-semibold text-slate-900 mt-1">{item.value || ''}</p>
                    </div>
                  ))}
                </div>

                <div>
                  <h3 className="text-sm font-bold text-slate-900 mb-4">Candidate Progress</h3>
                  {(() => {
                    const currentKey = resolveCandidateStage(slideInCandidate);
                    const synopsis = STAGE_SYNOPSIS[currentKey];
                    return (
                      <div className="bg-blue-50/70 border border-blue-100 rounded-xl p-3.5 mb-5">
                        <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Current stage</p>
                        <p className="text-sm font-bold text-slate-900 mt-0.5">{STAGE_LABELS[currentKey]}</p>
                        <p className="text-xs text-slate-600 mt-1.5 leading-relaxed">{synopsis.applicantDone}</p>
                        <p className="text-xs text-slate-600 leading-relaxed"><span className="font-semibold text-slate-700">What's next: </span>{synopsis.applicantNext}</p>
                      </div>
                    );
                  })()}
                  <div className="space-y-0">
                    {WORKFLOW_STAGES.map((stage, i) => {
                      const currentIdx = STAGE_ORDER.indexOf(resolveCandidateStage(slideInCandidate));
                      const isDone = i < currentIdx;
                      const isCurrent = i === currentIdx;
                      const StageIcon = STAGE_ICONS[stage.key];
                      return (
                        <div key={stage.key} className="flex items-start gap-3 pb-4 relative">
                          {i < WORKFLOW_STAGES.length - 1 && (
                            <div className={`absolute left-[15px] top-7 w-0.5 h-full ${isDone ? 'bg-emerald-200' : 'bg-slate-200'}`} />
                          )}
                          <div className={`p-1.5 rounded-full ${isDone ? 'bg-emerald-100' : isCurrent ? 'bg-blue-100' : 'bg-slate-100'}`}>
                            <StageIcon className={`h-3.5 w-3.5 ${isDone ? 'text-emerald-600' : isCurrent ? 'text-brand' : 'text-slate-400'}`} />
                          </div>
                          <div className="flex-1 min-w-0 pt-0.5">
                            <p className={`text-sm font-semibold ${isDone || isCurrent ? 'text-slate-900' : 'text-slate-500'}`}>{stage.label}</p>
                            {isCurrent && <p className="text-[11px] text-brand font-medium mt-0.5">Current stage</p>}
                            {isDone && <p className="text-[11px] text-slate-500 mt-0.5">{STAGE_SYNOPSIS[stage.key].applicantDone}</p>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {slideInCandidate.notes && (
                  <div>
                    <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Notes</h3>
                    <p className="text-sm text-slate-700 bg-slate-50 rounded-xl p-3">{slideInCandidate.notes}</p>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3 text-xs text-slate-500">
                  <div><span className="font-semibold">Created:</span> {slideInCandidate.createdAt ? new Date(slideInCandidate.createdAt).toLocaleDateString() : ''}</div>
                  <div><span className="font-semibold">Updated:</span> {slideInCandidate.updatedAt ? new Date(slideInCandidate.updatedAt).toLocaleDateString() : ''}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* CSV IMPORT WIZARD MODAL */}
      {showImportWizard && (
        <Modal onClose={() => setShowImportWizard(false)} ariaLabel="Import Candidates (CSV)">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90dvh] flex flex-col overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-slate-100 shrink-0">
              <div>
                <h2 className="text-base font-bold text-slate-900">Import Candidates (CSV)</h2>
                <p className="text-xs text-slate-500 mt-0.5">Step {importStep} of 5</p>
              </div>
              <button onClick={() => setShowImportWizard(false)} aria-label="Close" className="p-1.5 min-h-[44px] min-w-[44px] inline-flex items-center justify-center text-slate-400 hover:text-slate-600 rounded-lg cursor-pointer"><X className="h-5 w-5" /></button>
            </div>

            {/* Content */}
            <div className="p-5 flex-1 overflow-y-auto space-y-4">
              {/* Step 1: Select Program and Term */}
              {importStep === 1 && (
                <div className="space-y-4">
                  <div className="p-3 bg-blue-50 border border-blue-150 rounded-xl text-xs text-blue-800 font-semibold flex items-center gap-2">
                    <Info className="h-4.5 w-4.5 shrink-0" />
                    <span>Please assign a Program and Term for the imported candidates.</span>
                  </div>
                  <div className="space-y-1">
                    <label htmlFor="import-program" className="text-xs font-semibold text-slate-700">Select Program *</label>
                    <SearchableSelect
                      id="import-program"
                      ariaLabel="Program"
                      value={importProgramId}
                      onChange={(v) => {
                        setImportProgramId(v);
                        setImportTermId('');
                      }}
                      options={referrerPrograms.filter((p: any) => p.active !== false && (p.terms || p.intakes) && (p.terms || p.intakes).some((t: any) => t.isOpen !== false)).map((p: any) => ({
                        value: p.id,
                        label: `${p.name}${p.commissionRateSet === false ? ' — commission amount not set' : ''}`,
                        disabled: p.commissionRateSet === false,
                      }))}
                      placeholder="Search programs…"
                      clearLabel="Select a program..."
                      emptyLabel="No programs found"
                    />
                    {referrerPrograms.some((p: any) => p.commissionRateSet === false) && (
                      <p className="mt-1 text-[11px] text-amber-600">Programs marked "commission amount not set" can't accept candidates yet — your ISATS admin needs to set a commission amount for them on your referrer profile (it can be $0).</p>
                    )}
                  </div>
                  {importProgramId && (
                    <div className="space-y-1">
                      <label htmlFor="import-term" className="text-xs font-semibold text-slate-700">Select Term *</label>
                      <select
                        id="import-term"
                        value={importTermId}
                        onChange={(e) => setImportTermId(e.target.value)}
                        className="w-full px-3 py-3 md:py-2.5 text-base md:text-sm border border-slate-200 rounded-xl bg-white outline-none focus:ring-2 focus:ring-brand-primary/20"
                      >
                        <option value="">Select a term...</option>
                        {((referrerPrograms.find(p => p.id === importProgramId)?.terms || referrerPrograms.find(p => p.id === importProgramId)?.intakes) || []).filter((t: any) => typeof t === 'string' || t.isOpen !== false).map((t: any) => (
                          <option key={t.termId || t} value={t.termId || t}>{t.label || t}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              )}

              {/* Step 2: Upload CSV */}
              {importStep === 2 && (
                <div className="space-y-4">
                  <div className="border-2 border-dashed border-slate-200 hover:border-brand rounded-2xl p-8 text-center transition cursor-pointer relative bg-slate-50/50">
                    <Upload className="h-10 w-10 text-slate-400 mx-auto mb-3" />
                    <p className="text-sm font-semibold text-slate-700">Choose CSV File</p>
                    <p className="text-xs text-slate-500 mt-1">Make sure it contains a header row with name and email</p>
                    <input type="file" accept=".csv" onChange={handleCSVUpload} className="absolute inset-0 opacity-0 cursor-pointer w-full h-full" />
                  </div>
                </div>
              )}

              {/* Step 3: Map Columns */}
              {importStep === 3 && (
                <div className="space-y-4">
                  <p className="text-xs text-slate-500 font-semibold mb-3">Map CSV headers to candidate database fields:</p>
                  {['firstName', 'lastName', 'phone', 'email'].map((field) => {
                    const label = field === 'firstName' ? 'First Name *' : field === 'lastName' ? 'Last Name' : field === 'phone' ? 'Phone Number' : 'Email *';
                    return (
                      <div key={field} className="grid grid-cols-2 gap-3 items-center border-b border-slate-100 pb-2">
                        <label htmlFor={`import-map-${field}`} className="text-xs font-bold text-slate-700">{label}</label>
                        <select
                          id={`import-map-${field}`}
                          value={importMapping[field]}
                          onChange={(e) => setImportMapping({ ...importMapping, [field]: parseInt(e.target.value) })}
                          className="px-2 py-3 md:py-1.5 text-base md:text-xs border border-slate-200 bg-white rounded-lg outline-none"
                        >
                          <option value="-1">-- Unmapped --</option>
                          {importHeaders.map((h, idx) => (
                            <option key={idx} value={idx}>{h}</option>
                          ))}
                        </select>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Step 4: Preview and Validate */}
              {importStep === 4 && (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { label: 'Total CSV Rows', value: importRows.length },
                      { label: 'Ready to Import', value: importRows.filter(row => row[importMapping.firstName] && row[importMapping.email]).length },
                      { label: 'Invalid / Skipped', value: importRows.filter(row => !row[importMapping.firstName] || !row[importMapping.email]).length },
                    ].map((s, idx) => (
                      <div key={idx} className="bg-slate-50 rounded-xl p-3 text-center border border-slate-200">
                        <p className="text-[10px] text-slate-400 uppercase font-semibold">{s.label}</p>
                        <p className="text-lg font-bold text-slate-800 mt-1">{s.value}</p>
                      </div>
                    ))}
                  </div>

                  <div className="border border-slate-200 rounded-xl overflow-hidden max-h-48 overflow-y-auto">
                    <table className="w-full text-xs text-left">
                      <thead className="bg-slate-50 text-slate-500 font-semibold border-b border-slate-200">
                        <tr>
                          <th className="px-3 py-2">Row</th>
                          <th className="px-3 py-2">Name</th>
                          <th className="px-3 py-2">Email</th>
                          <th className="px-3 py-2">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {importRows.slice(0, 50).map((row, idx) => {
                          const firstName = importMapping.firstName >= 0 ? row[importMapping.firstName] : '';
                          const email = importMapping.email >= 0 ? row[importMapping.email] : '';
                          const isValid = !!firstName && !!email;
                          return (
                            <tr key={idx} className={isValid ? 'text-slate-700' : 'bg-red-50/40 text-red-700'}>
                              <td className="px-3 py-2 font-mono text-[10px]">{idx + 2}</td>
                              <td className="px-3 py-2 font-semibold">{firstName || <span className="italic text-red-500">Missing</span>}</td>
                              <td className="px-3 py-2 font-mono">{email || <span className="italic text-red-500">Missing</span>}</td>
                              <td className="px-3 py-2">
                                <span className={`px-2 py-0.5 rounded text-[11px] font-bold ${isValid ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                                  {isValid ? 'VALID' : 'INVALID'}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {importRows.length > 50 && (
                    <p className="text-[11px] text-slate-500 italic text-center">Showing first 50 rows for preview.</p>
                  )}
                </div>
              )}

              {/* Step 5: Import Summary & Complete */}
              {importStep === 5 && importResult && (
                <div className="space-y-4">
                  <div className="p-4 bg-emerald-50 border border-emerald-150 rounded-2xl text-center space-y-2">
                    <CheckCircle className="h-10 w-10 text-emerald-600 mx-auto" />
                    <h3 className="font-bold text-emerald-900 text-sm">Import Completed Successfully!</h3>
                    <p className="text-xs text-emerald-700 font-medium">
                      Successfully imported <strong className="font-extrabold">{importResult.successCount}</strong> candidates to {referrerPrograms.find(p => p.id === importProgramId)?.name}.
                    </p>
                    {importResult.skippedCount > 0 && (
                      <p className="text-xs text-amber-700 font-medium">
                        Skipped <strong className="font-extrabold">{importResult.skippedCount}</strong> rows due to validation failures/duplicates.
                      </p>
                    )}
                  </div>

                  {importResult.errors && importResult.errors.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs font-bold text-slate-700">Detailed Skip Log:</p>
                      <div className="border border-slate-200 rounded-xl overflow-hidden max-h-48 overflow-y-auto">
                        <table className="w-full text-xs text-left">
                          <thead className="bg-slate-50 text-slate-500 border-b border-slate-200">
                            <tr>
                              <th className="px-3 py-2">Email</th>
                              <th className="px-3 py-2">Reason</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100 font-medium">
                            {importResult.errors.map((err: any, idx: number) => (
                              <tr key={idx} className="text-slate-700 hover:bg-slate-50">
                                <td className="px-3 py-2 font-mono text-[10px]">{err.email}</td>
                                <td className="px-3 py-2 text-red-600 font-semibold">{err.reason}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between p-5 border-t border-slate-100 shrink-0">
              <div>
                {importStep > 1 && importStep < 5 && (
                  <button
                    onClick={() => setImportStep(prev => prev - 1)}
                    className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-600 rounded-xl text-xs font-bold transition cursor-pointer flex items-center space-x-1"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    <span>Back</span>
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                {importStep < 5 && (
                  <button onClick={() => setShowImportWizard(false)} className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-50 rounded-xl transition cursor-pointer">Cancel</button>
                )}
                {importStep === 1 && (
                  <button
                    onClick={() => setImportStep(2)}
                    disabled={!importProgramId || !importTermId}
                    className="px-5 py-2 btn-primary  text-white rounded-xl text-xs font-bold transition disabled:opacity-40 cursor-pointer"
                  >
                    Next
                  </button>
                )}
                {importStep === 3 && (
                  <button
                    onClick={() => setImportStep(4)}
                    disabled={importMapping.firstName < 0 || importMapping.email < 0}
                    className="px-5 py-2 btn-primary  text-white rounded-xl text-xs font-bold transition disabled:opacity-40 cursor-pointer"
                  >
                    Preview
                  </button>
                )}
                {importStep === 4 && (
                  <button
                    onClick={executeCandidateImport}
                    disabled={!!restricted || importLoading || importRows.filter(row => row[importMapping.firstName] && row[importMapping.email]).length === 0}
                    title={restricted ? REFERRER_RESTRICTED_NOTICE : undefined}
                    className="px-5 py-2 bg-success hover:bg-[color-mix(in_srgb,var(--color-success)_90%,#000)] disabled:bg-success/40 text-white rounded-xl text-xs font-bold transition flex items-center space-x-1 cursor-pointer"
                  >
                    {importLoading ? <span>Importing...</span> : <span>Import Candidates ({importRows.filter(row => row[importMapping.firstName] && row[importMapping.email]).length})</span>}
                  </button>
                )}
                {importStep === 5 && (
                  <button onClick={() => setShowImportWizard(false)} className="px-5 py-2 btn-primary  text-white rounded-xl text-xs font-bold transition cursor-pointer">Done</button>
                )}
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
